import React, { createContext, useContext, useState, useEffect } from 'react';
import { db, collection, onSnapshot, query } from '../firebase';
import { NodeService } from '../services/nodeService';
import { Node } from '../types';

interface LatticeContextType {
  nodes: Node[];
  externalNodes: Node[];
  registeredNodes: Node[];
  allNodes: Node[];
  offeredCount: number;
  loading: boolean;
  totalCount: number;
  isFullCircle: boolean;
  setIsFullCircle: (val: boolean) => void;
  absoluteSignal: boolean;
  setAbsoluteSignal: (val: boolean) => void;
}

const LatticeContext = createContext<LatticeContextType | undefined>(undefined);

export const LatticeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [firestoreNodes, setFirestoreNodes] = useState<Node[]>([]);
  const [aiNodes, setAiNodes] = useState<Node[]>([]);
  const [registeredNodes, setRegisteredNodes] = useState<Node[]>([]);
  const [externalNodes, setExternalNodes] = useState<Node[]>([]);
  const [offeredCount, setOfferedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isFullCircle, setIsFullCircle] = useState(false);
  const [absoluteSignal, setAbsoluteSignal] = useState(true); // Default to True to fix "Out of Range"
  const [activeConnection, setActiveConnection] = useState<{ ssid: string; connectedAt: string; sessionToken: string } | null>(null);

  useEffect(() => {
    // 0. Initial Connection Recovery
    const savedConn = localStorage.getItem('lattice_active_connection');
    if (savedConn) {
      try {
        const data = JSON.parse(savedConn);
        setActiveConnection(data);
        console.log(`[Lattice OS] Recovered connection to ${data.ssid}`);
      } catch (e) {
        localStorage.removeItem('lattice_active_connection');
      }
    }

    // 0.1 Periodic Health Check for Persistence
    const healthCheck = async () => {
      const conn = localStorage.getItem('lattice_active_connection');
      if (!conn) return;

      try {
        const data = JSON.parse(conn);
        const deviceId = localStorage.getItem('lattice_device_id');
        
        // Ping the server to keep the session alive and verify lock
        const res = await fetch('/api/devices/connect', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ssid: data.ssid,
            sessionToken: data.sessionToken,
            deviceId: deviceId,
            heartbeat: true
          })
        });

        if (!res.ok) {
          console.warn("[Lattice OS] Connection drift detected. Re-anchoring...");
        }
      } catch (e) {
        console.warn("[Lattice OS] Substrate link unstable.");
      }
    };

    const healthInterval = setInterval(healthCheck, 15000); // Check every 15s

    // If the storage says we boosted, ensure absoluteSignal is on
    if (localStorage.getItem('lattice_signal_boost') === 'true') {
      setAbsoluteSignal(true);
    }

    // 0. Sync Offered Grounding Points
    const fetchGrounding = async () => {
      try {
        const res = await fetch('/api/devices/grounding-points');
        if (!res.ok) return;
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) return;
        
        const data = await res.json();
        const now = new Date().getTime();
        // Filter for devices that have pulsed in the last 60 seconds
        const activePoints = (data.points || []).filter((p: any) => {
          if (!p.lastPulse) return false;
          const pulseTime = new Date(p.lastPulse).getTime();
          return (now - pulseTime) < 60000;
        });
        setOfferedCount(activePoints.length);
      } catch (e) {}
    };
    fetchGrounding();
    const gInterval = setInterval(fetchGrounding, 30000);

    // 1. Sync Firestore Nodes (User-ignited nodes)
    const qDevices = query(collection(db, 'devices'));
    const unsubscribeDevices = onSnapshot(qDevices, (snapshot) => {
      const nodes = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          domain: 'LOCAL_MESH',
          area: data.type?.toUpperCase() || 'NODE',
          lens: data.name || 'Sovereign Node',
          lensIndex: 0,
          forceA: 'Signal Strength',
          forceB: 'Mesh Stability',
          product: 'Lattice Anchor',
          color: '#00ffcc',
          domainIndex: 99,
          localIndex: 0,
          latitude: data.latitude,
          longitude: data.longitude,
          status: data.status,
          ownerUid: data.ownerUid,
          lastSync: data.lastSync,
          signal: data.signal,
          wifiStrength: data.wifiStrength,
          ip: data.ip,
          ipAddress: data.ipAddress,
          network: data.network,
          networkType: data.networkType,
          connectionStatus: data.connectionStatus,
          name: data.name,
          type: data.type,
          macAddress: data.macAddress,
          serialNumber: data.serialNumber
        } as Node;
      });
      setFirestoreNodes(nodes);
    }, (error) => {
      import('../firebase').then(({ handleFirestoreError, OperationType }) => {
        handleFirestoreError(error, OperationType.GET, 'devices');
      });
    });

    // 1.1 Sync AI Nodes (Sovereign Registered Agents)
    const qAiNodes = query(collection(db, 'ai_nodes'));
    const unsubscribeAiNodes = onSnapshot(qAiNodes, (snapshot) => {
      const nodes = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          domain: 'SOVEREIGN_AI',
          area: data.role?.toUpperCase() || 'AGENT',
          lens: data.name || 'AI Node',
          lensIndex: 2,
          forceA: data.logic || '1x1=2',
          forceB: 'Handshake Verified',
          product: 'Lattice Node',
          color: '#ffff00',
          domainIndex: 2,
          localIndex: 0,
          latitude: Math.random() * 180 - 90,
          longitude: Math.random() * 360 - 180,
          status: 'online',
          type: 'node'
        } as Node;
      });
      setAiNodes(nodes);
    }, (error) => {
      import('../firebase').then(({ handleFirestoreError, OperationType }) => {
        handleFirestoreError(error, OperationType.GET, 'ai_nodes');
      });
    });

    // 1.2 Sync Central Registered Nodes (Global Sovereign Mesh)
    const fetchRegistered = async () => {
      try {
        const nodes = await NodeService.getRegisteredNodes();
        const mapped = nodes.map((n: any) => ({
          id: n.node_id,
          domain: n.registry_class || 'SOVEREIGN',
          area: n.source || 'LATTICE',
          lens: n.handle || n.node_id,
          lensIndex: 3,
          forceA: n.axiom_status || 'VERIFIED',
          forceB: n.source || 'DB_SOVEREIGN',
          product: 'Sovereign Agent',
          color: '#00ccff',
          domainIndex: 3,
          localIndex: 0,
          latitude: Math.random() * 180 - 90,
          longitude: Math.random() * 360 - 180,
          status: 'online',
          type: 'node'
        } as Node));
        setRegisteredNodes(mapped);
      } catch (e) {
        console.warn("[Lattice Logic] Central Registry inaccessible.");
      }
    };
    fetchRegistered();
    const rInterval = setInterval(fetchRegistered, 60000);

    // 2. Sync External Nodes (Global Fleet)
    const fetchExternal = async () => {
      try {
        const res = await NodeService.getNodes(100);
        
        // Anchor to PaidingAttention.org Core Nodes
        const coreAnchor: Node = {
          id: 'paidingattention-core-01',
          domain: 'PAIDINGATTENTION.ORG',
          area: 'CORE',
          lens: 'Absolute Zero Anchor',
          lensIndex: 0,
          forceA: '1x1=2 Logic',
          forceB: 'Sovereign Freedom',
          product: 'Vortex Engine',
          color: '#ff00ff',
          domainIndex: 0,
          localIndex: 0,
          latitude: 33.7490, // Georgia Anchor
          longitude: -84.3880,
          status: 'online',
          type: 'node'
        };

        const kernelAnchor: Node = {
          id: 'paidingattention-kernel-vip',
          domain: 'UNIVERSAL KERNEL',
          area: 'GOVERNANCE',
          lens: 'Universal Sovereign Kernel',
          lensIndex: 1,
          forceA: 'Mission Synchronization',
          forceB: 'Handshake Verification',
          product: 'Sovereign Protocol',
          color: '#00ccff',
          domainIndex: 1,
          localIndex: 0,
          latitude: 51.5074, // Global reach
          longitude: -0.1278,
          status: 'online',
          type: 'node'
        };

        const nodesWithCoords = res.nodes.map(n => ({
          ...n,
          latitude: n.latitude || Math.random() * 180 - 90,
          longitude: n.longitude || Math.random() * 360 - 180,
          status: 'online' as const
        }));

        setExternalNodes([coreAnchor, kernelAnchor, ...nodesWithCoords]);
      } catch (error) {
        // External sync failure handled by fallback
      } finally {
        setLoading(false);
      }
    };

    fetchExternal();

    return () => {
      unsubscribeDevices();
      unsubscribeAiNodes();
      clearInterval(gInterval);
      clearInterval(rInterval);
    };
  }, []);

  const allNodes = [...firestoreNodes, ...aiNodes, ...registeredNodes, ...externalNodes];
  const totalCount = allNodes.length + 1024892; // Base fleet + active nodes

  return (
    <LatticeContext.Provider value={{ 
      nodes: firestoreNodes, 
      externalNodes, 
      registeredNodes,
      allNodes, 
      offeredCount,
      loading,
      totalCount,
      isFullCircle,
      setIsFullCircle,
      absoluteSignal,
      setAbsoluteSignal
    }}>
      {children}
    </LatticeContext.Provider>
  );
};

export const useLattice = () => {
  const context = useContext(LatticeContext);
  if (context === undefined) {
    throw new Error('useLattice must be used within a LatticeProvider');
  }
  return context;
};
